/**
 * My own book on Home: the week by day, this month's commission, the pipeline in five lanes,
 * reasons to call customers this week, and personal bests. Dates read MM-DD-YYYY.
 */
import { toUsDate, type Cockpit } from "@tlcrm/shared";
import { Link } from "react-router";

import { KindDot, type RecordKind } from "../../components/kit";
import { compactMoney } from "../../lib/chart";
import { money } from "../../lib/format";

const DAY = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const weekday = (iso: string) => {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y!, m! - 1, d!).getDay();
};
const todayIso = () => new Date().toLocaleDateString("en-CA");

export function WeekByDay({ cockpit }: { cockpit: Cockpit }) {
  const days = cockpit.weekByDay;
  const max = Math.max(1, ...days.map((d) => d.value));
  const today = todayIso();
  const best = cockpit.bests.week;
  return (
    <section className="card weekbars">
      <div className="card-head">
        <h3>My week by day</h3>
        <span className="card-sub">Closed Won premium</span>
      </div>
      <div className="card-body">
        <div
          className="weekbars-plot"
          role="img"
          aria-label={days.map((d) => `${DAY[weekday(d.date)]} ${money(d.value)}`).join(", ")}
        >
          {days.map((d) => (
            <div key={d.date} className={`weekbars-day${d.date === today ? " is-today" : ""}`}>
              <span className="weekbars-v mono">{d.value ? compactMoney(d.value) : ""}</span>
              <span
                className={`weekbars-bar${d.value ? " has-v" : ""}`}
                style={{ height: `${(d.value / max) * 100}%` }}
              />
              <span className="weekbars-label">{DAY[weekday(d.date)]}</span>
            </div>
          ))}
        </div>
        <p className="card-note">
          {best ? (
            <>
              Your best week: <b className="mono">{money(best.amount)}</b>, week of{" "}
              <span className="mono">{toUsDate(best.date)}</span>.
            </>
          ) : (
            "Your best week shows here after your first sale."
          )}
        </p>
      </div>
    </section>
  );
}

export function CommissionMonth({ c }: { c: Cockpit["commission"] }) {
  const total = c.paid + c.waiting + c.chargedBack;
  const month = new Date(`${c.month.start}T12:00:00`).toLocaleDateString("en-US", {
    month: "long",
  });
  const part = (n: number) => (total ? `${(n / total) * 100}%` : "0");
  return (
    <section className="card commission">
      <div className="card-head">
        <h3>My commission</h3>
        <span className="card-sub">{month} sales</span>
        <Link to="/commissions" className="card-link">
          Statement
        </Link>
      </div>
      <div className="card-body">
        <span className="eyebrow">Expected, still active</span>
        <div className="commission-v mono">{money(c.expected, true)}</div>
        <div className="commission-bar" aria-hidden="true">
          <span className="is-paid" style={{ width: part(c.paid) }} />
          <span className="is-waiting" style={{ width: part(c.waiting) }} />
          <span className="is-back" style={{ width: part(c.chargedBack) }} />
        </div>
        <dl className="commission-legend">
          <dt>
            <i className="is-paid" />
            Paid
          </dt>
          <dd className="mono">{money(c.paid, true)}</dd>
          <dt>
            <i className="is-waiting" />
            Waiting on carrier
          </dt>
          <dd className="mono">{money(c.waiting, true)}</dd>
          <dt>
            <i className="is-back" />
            Charged back
          </dt>
          <dd className="mono">
            {c.chargedBack ? `−${money(c.chargedBack, true)}` : money(0, true)}
          </dd>
        </dl>
        <p className="card-note">
          {c.nextPayDate ? (
            <>
              Next expected pay <b className="mono">{toUsDate(c.nextPayDate)}</b>
              {c.payDateEstimated && <span className="t3"> · estimated, rule not confirmed</span>}
            </>
          ) : (
            "No pay date coming up for this month's sales."
          )}
        </p>
      </div>
    </section>
  );
}

const LANE_KIND: Record<Cockpit["pipeline"][number]["key"], RecordKind> = {
  new: "lead",
  working: "lead",
  contacted: "lead",
  issued: "policy",
  won: "policy",
};

export function Pipeline({ lanes }: { lanes: Cockpit["pipeline"] }) {
  return (
    <section className="card pipeline">
      <div className="card-head">
        <h3>My pipeline</h3>
        <span className="card-sub">Leads to sold, this week</span>
        <Link to="/pipeline" className="card-link">
          Open board
        </Link>
      </div>
      <div className="lanes">
        {lanes.map((l) => (
          <div key={l.key} className="lane">
            <div className="lane-head">
              <KindDot kind={LANE_KIND[l.key]} />
              <b>{l.label}</b>
              <span className="mono">{l.count}</span>
              {l.premium !== null && (
                <span className="mono lane-sum">{compactMoney(l.premium)}</span>
              )}
            </div>
            {l.cards.slice(0, 3).map((c) => (
              <Link key={c.id} to={c.href} className="lane-card">
                <b>{c.name}</b>
                <span>{c.meta}</span>
              </Link>
            ))}
            {l.count > 3 && <span className="lane-more">{l.count - 3} more</span>}
            {l.count === 0 && <span className="lane-more">None</span>}
          </div>
        ))}
      </div>
    </section>
  );
}

const dayAndDate = (iso: string) => `${DAY[weekday(iso)]} ${toUsDate(iso).slice(0, 5)}`;

export function Moments({ m }: { m: Cockpit["moments"] }) {
  const cols = [
    {
      title: "Birthdays",
      sub: "next 7 days",
      items: m.birthdays.map((b) => ({
        key: b.contactId,
        to: `/contacts/${b.contactId}`,
        name: b.name,
        detail: `${dayAndDate(b.date)} · turns ${b.turning}`,
      })),
      empty: "No birthdays this week.",
    },
    {
      title: "Going effective",
      sub: "next 7 days",
      items: m.goingEffective.map((g) => ({
        key: g.policyId,
        to: `/policies/${g.policyId}`,
        name: g.name,
        detail: dayAndDate(g.date),
      })),
      empty: "Nothing going effective.",
    },
    {
      title: "Drafts coming",
      sub: "next 3 days",
      items: m.draftsSoon.map((d) => ({
        key: d.policyId,
        to: `/policies/${d.policyId}`,
        name: d.name,
        detail: `${dayAndDate(d.date)}${d.monthly ? ` · ${money(d.monthly, true)}` : ""}`,
      })),
      empty: "No drafts in the next 3 days.",
    },
  ];
  return (
    <section className="card moments">
      <div className="card-head">
        <h3>Reasons to call your book</h3>
        <span className="card-sub">Customers, not leads</span>
      </div>
      <div className="moments-cols">
        {cols.map((c) => (
          <div key={c.title} className="moments-col">
            <div className="moments-head">
              <b>{c.title}</b>
              <span className="t3">{c.sub}</span>
              <span className="mono">{c.items.length}</span>
            </div>
            {c.items.length === 0 ? (
              <p className="t3 moments-empty">{c.empty}</p>
            ) : (
              <ul>
                {c.items.slice(0, 4).map((i) => (
                  <li key={i.key}>
                    <Link to={i.to}>{i.name}</Link>
                    <span className="mono">{i.detail}</span>
                  </li>
                ))}
                {c.items.length > 4 && <li className="t3">{c.items.length - 4} more</li>}
              </ul>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}

export function Bests({ b, streakBest }: { b: Cockpit["bests"]; streakBest: number }) {
  const month = (iso: string) =>
    new Date(`${iso}T12:00:00`).toLocaleDateString("en-US", { month: "long", year: "numeric" });
  const cells = [
    {
      label: "Best day",
      value: b.day ? money(b.day.amount) : "–",
      sub: b.day ? toUsDate(b.day.date) : "Not yet",
    },
    {
      label: "Best week",
      value: b.week ? money(b.week.amount) : "–",
      sub: b.week ? `Week of ${toUsDate(b.week.date)}` : "Not yet",
    },
    {
      label: "Best month",
      value: b.month ? money(b.month.amount) : "–",
      sub: b.month ? month(b.month.date) : "Not yet",
    },
    {
      label: "Biggest policy",
      value: b.biggest ? money(b.biggest.amount, true) : "–",
      sub: b.biggest
        ? `${[b.biggest.carrier, b.biggest.product].filter(Boolean).join(" ")} · ${toUsDate(b.biggest.date)}`
        : "Not yet",
    },
    { label: "Placed this year", value: String(b.placedThisYear), sub: "Closed Won policies" },
    {
      label: "Longest streak",
      value: `${streakBest} ${streakBest === 1 ? "week" : "weeks"}`,
      sub: "Weeks in a row with a sale",
    },
  ];
  return (
    <section className="card bests">
      <div className="card-head">
        <h3>Personal bests</h3>
        <span className="card-sub">The numbers to beat</span>
      </div>
      <div className="bests-grid">
        {cells.map((c) => (
          <div key={c.label} className="bests-cell">
            <span className="eyebrow">{c.label}</span>
            <span className="mono bests-v">{c.value}</span>
            <span className="t3">{c.sub}</span>
          </div>
        ))}
      </div>
    </section>
  );
}
